import type { ActionplanStatus } from "api/generated";
import { formatLongDate, toDutchDateString } from "shared/utils/dateHelpers";

/**
 * Whether a plan's deadline has passed. A plan without a deadline never expires.
 *
 * Display only: a passed deadline is surfaced as a banner and a badge, and
 * restricts nothing.
 *
 * @invariant ACTIONPLAN-DEADLINE-IS-INFORMATIONAL-001
 */
export const isActionplanExpired = (deadline: string | null): boolean => {
  if (!deadline) {
    return false;
  }

  return new Date(deadline) < new Date();
};

/** Which of the four date wordings a plan carries, and the date it names. */
export type ActionplanDateLabel = {
  key: "completed" | "expired" | "expires" | "plain";
  date: string;
} | null;

/**
 * What a list row and the fly-out it opens say about a plan's date. One place,
 * because the two have to name the same day
 * (@invariant ACTIONPLAN-COMPLETED-ROW-DATE-001) and they drifted apart once
 * already.
 *
 * Three readings, in the order the plan travels:
 * - still the student's (`requested`/`active`): the clock runs, so the deadline
 *   is worded as expiring or expired.
 * - handed in (`pending_review`): the clock stopped and the plan is the
 *   teacher's, so the date is stated plainly rather than as a countdown.
 * - finished (`graded`): the date is the day the teacher's verdict closed it,
 *   falling back to the deadline for plans stamped before that column existed.
 *
 * Both dates arrive raw and are formatted here, on the school clock: a plan
 * graded at half past midnight in Amsterdam must not read as the day before
 * because the browser sits in another timezone.
 */
export const actionplanDateLabel = (
  status: ActionplanStatus,
  dates: { completedAt: string | null; deadlineAt: string | null },
): ActionplanDateLabel => {
  if (status === "graded") {
    const closedOn = dates.completedAt ?? dates.deadlineAt;

    return closedOn === null
      ? null
      : { key: "completed", date: formatLongDate(closedOn) };
  }

  if (dates.deadlineAt === null) {
    return null;
  }

  if (status === "pending_review") {
    return { key: "plain", date: formatLongDate(dates.deadlineAt) };
  }

  return {
    key: isActionplanExpired(dates.deadlineAt) ? "expired" : "expires",
    date: formatLongDate(dates.deadlineAt),
  };
};

/**
 * The time a deadline runs until when the teacher or student left the time
 * field empty: the end of that day, so "af op de 15e" keeps the 15th
 * (ACTIONPLAN-DEADLINE-WITHOUT-TIME-IS-END-OF-DAY-001).
 */
const END_OF_DAY = "23:59:59";

/**
 * Combines the form's two fields into the single datetime the API takes, the
 * way the task form does. The date arrives Dutch ("15-09-2026") because that is
 * what CInputDateSelect works in; the API wants it the other way round.
 */
export const toActionplanDeadline = (
  date: string | null,
  time: string | null,
): string | null => {
  if (!date) {
    return null;
  }

  const [day, month, year] = date.split("-");

  return `${year}-${month}-${day} ${time || END_OF_DAY}`;
};

/**
 * The times this app writes when nobody picked one: the end of the day, and
 * midnight on rows written before that rule
 * (ACTIONPLAN-DEADLINE-WITHOUT-TIME-IS-END-OF-DAY-001). Showing "23:59" back
 * would invent a time the teacher never set.
 */
const IMPLICIT_TIMES = ["23:59", "00:00"];

/**
 * Splits a stored deadline into the two fields the pickers work in: a Dutch date
 * ("15-09-2026") and a time, or `null` for a time nobody picked.
 */
export const splitActionplanDeadline = (
  deadlineAt: string | null,
): { date: string | null; time: string | null } => {
  if (!deadlineAt) {
    return { date: null, time: null };
  }

  const [date, time] = toDutchDateString(deadlineAt, true).split(" ");

  return {
    date: date ?? null,
    time: IMPLICIT_TIMES.includes(time) ? null : (time ?? null),
  };
};
